import React, { useCallback, useRef, useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useMailWebSocket } from '../../hooks/useMailWebSocket';
import Sidebar from './Sidebar';
import HeaderBar from './HeaderBar';
import AstraeaBubble from './AstraeaBubble';
import ProfileCompletionBanner from '../ProfileCompletionBanner';
import SupportModeBanner from './SupportModeBanner';
import VoiceIndicator from '../voice/VoiceIndicator';
import MicPreferencePrompt from '../voice/MicPreferencePrompt';
import CalendarNotifications from '../CalendarNotifications';
import { useVoiceCommands } from '../../context/VoiceCommandContext';
import { useGlobalSoftphone } from '../../hooks/useGlobalSoftphone';
import { ringCentralService } from '../../services/ringCentralService';
import NotificationCenter from '../notifications/NotificationCenter';
import IncomingCallPopup from '../IncomingCallPopup';
import PhoneSidebar from '../phone/PhoneSidebar';
import PostCallPanel from '../PostCallPanel';

const Layout = ({ children }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const isFullBleed = ["/mail", "/sigil"].includes(location.pathname);
  const { setEnabled } = useVoiceCommands();
  const ringtoneRef = useRef(null);
  const notificationRef = useRef(null);
  const [incomingCall, setIncomingCall] = useState(null);
  const [postCallData, setPostCallData] = useState(null);
  const [phoneOpen, setPhoneOpen] = useState(false);
  const [phoneCall, setPhoneCall] = useState(null);
  const [visageCall, setVisageCall] = useState(null);
  const visageCallTimeoutRef = useRef(null);

  // Set up ringtone audio
  useEffect(() => {
    ringtoneRef.current = new Audio('/assets/audio/ringtone.wav');
    ringtoneRef.current.loop = true;
    return () => {
      if (ringtoneRef.current) {
        ringtoneRef.current.pause();
        ringtoneRef.current = null;
      }
    };
  }, []);

  // Global incoming call handler — works on every page
  const handleIncomingCall = useCallback((callData) => {
    console.log('[Layout] Incoming call:', callData);
    const callerInfo = callData?.from_name || callData?.from_number || callData?.caller?.name || callData?.caller?.phoneNumber || 'Unknown';

    // Show in-page banner (this always works, no browser restrictions)
    setIncomingCall({ callerInfo, callData });

    // Try ringtone (may be blocked by autoplay policy until user clicks)
    if (ringtoneRef.current) {
      ringtoneRef.current.currentTime = 0;
      ringtoneRef.current.play().catch((err) => {
        console.warn('[Layout] Ringtone blocked by autoplay policy:', err.message);
      });
    }

    // Also try browser notification for when tab is in background
    if ('Notification' in window && Notification.permission === 'granted') {
      if (notificationRef.current) notificationRef.current.close();
      notificationRef.current = new Notification('Incoming Call', {
        body: 'Call from ' + callerInfo,
        icon: '/assets/images/virgo_logo.jpg',
        tag: 'virgo-incoming-call',
        requireInteraction: true,
        silent: false,
      });
      notificationRef.current.onclick = () => {
        window.focus();
      };
    }
  }, []);

  const handleCallEnded = useCallback((callId, callSummary = {}) => {
    setIncomingCall(null);
    if (ringtoneRef.current) {
      ringtoneRef.current.pause();
      ringtoneRef.current.currentTime = 0;
    }
    if (notificationRef.current) {
      notificationRef.current.close();
      notificationRef.current = null;
    }
    if (callSummary && callSummary.from_number) {
      setPostCallData({
        ...callSummary,
        endedAt: new Date().toISOString()
      });
    }
  }, []);

  // Answer button — this IS a user gesture so audio + navigation work
  const handleAnswerClick = useCallback(() => {
    // Play ringtone now (user gesture unlocks audio)
    if (ringtoneRef.current) {
      ringtoneRef.current.pause();
      ringtoneRef.current.currentTime = 0;
    }
    if (notificationRef.current) {
      notificationRef.current.close();
      notificationRef.current = null;
    }
    setIncomingCall(null);
    navigate('/voip');
  }, []);

  const handleDismissCall = useCallback(() => {
    setIncomingCall(null);
    if (ringtoneRef.current) {
      ringtoneRef.current.pause();
      ringtoneRef.current.currentTime = 0;
    }
    if (notificationRef.current) {
      notificationRef.current.close();
      notificationRef.current = null;
    }
  }, []);

  // Visage call handler
  const handleVisageCall = useCallback((data) => {
    console.log('[Layout] Visage incoming call:', data);
    setVisageCall(data);

    // Try ringtone
    if (ringtoneRef.current) {
      ringtoneRef.current.currentTime = 0;
      ringtoneRef.current.play().catch(() => {});
    }

    // Browser notification
    if ('Notification' in window && Notification.permission === 'granted') {
      const n = new Notification('Visage Meeting Invite', {
        body: `${data.caller_name} is calling you to join ${data.room_name}`,
        icon: '/assets/images/virgo_logo.jpg',
        tag: 'visage-call',
        requireInteraction: true,
      });
      n.onclick = () => window.focus();
    }

    // Auto-dismiss after 30 seconds
    if (visageCallTimeoutRef.current) clearTimeout(visageCallTimeoutRef.current);
    visageCallTimeoutRef.current = setTimeout(() => {
      setVisageCall(null);
      if (ringtoneRef.current) {
        ringtoneRef.current.pause();
        ringtoneRef.current.currentTime = 0;
      }
    }, 30000);
  }, []);

  const handleAcceptVisageCall = useCallback(() => {
    if (ringtoneRef.current) {
      ringtoneRef.current.pause();
      ringtoneRef.current.currentTime = 0;
    }
    if (visageCallTimeoutRef.current) clearTimeout(visageCallTimeoutRef.current);
    const roomId = visageCall?.room_id;
    setVisageCall(null);
    if (roomId) navigate(`/visage?join=${roomId}`);
  }, [visageCall, navigate]);

  const handleDeclineVisageCall = useCallback(() => {
    setVisageCall(null);
    if (ringtoneRef.current) {
      ringtoneRef.current.pause();
      ringtoneRef.current.currentTime = 0;
    }
    if (visageCallTimeoutRef.current) clearTimeout(visageCallTimeoutRef.current);
  }, []);

  // Global SIP registration — keeps the phone online on all pages
  const handleSipIncomingCall = useCallback((callInfo, session) => {
    // SIP call arrived — merge with existing incoming call handler
    const callerInfo = callInfo?.fromName || callInfo?.from || 'Unknown';
    handleIncomingCall({
      from_name: callInfo?.fromName,
      from_number: callInfo?.from,
      caller: { name: callerInfo, phoneNumber: callInfo?.from },
      via: 'sip'
    });
  }, [handleIncomingCall]);

  const { sipState, isRegistered: sipRegistered } = useGlobalSoftphone({
    onIncomingSipCall: handleSipIncomingCall,
    onSipCallEnded: handleCallEnded,
  });

  // Set up VirgoNative bridge for Android phone number injection
  useEffect(() => {
    window.VirgoNative = window.VirgoNative || {};
    window.VirgoNative.onPhoneNumber = (number) => {
      console.log('[Layout] VirgoNative.onPhoneNumber:', number);
      // Store the number for the VoipPage to pick up
      sessionStorage.setItem('virgo_dial_number', number);
      // Dispatch event so VoipPage can react
      window.dispatchEvent(new CustomEvent('virgo-dial-number', { detail: { number } }));
    };

    // dialNumber — actually places a SIP call via RingCentral softphone
    // Called from Android when Astraea issues a "call [name]" command
    window.VirgoNative.dialNumber = async (number) => {
      console.log('[Layout] VirgoNative.dialNumber:', number);
      if (!number) return;

      // Store number so VoipPage shows it in the UI
      sessionStorage.setItem('virgo_dial_number', number);

      if (ringCentralService.getIsRegistered()) {
        console.log('[Layout] SIP registered — placing call to', number);
        try {
          // Pre-acquire audio stream (may help bypass user gesture requirement)
          let audioStream = null;
          try {
            audioStream = await navigator.mediaDevices.getUserMedia({ audio: true });
            console.log('[Layout] Audio stream pre-acquired');
          } catch (audioErr) {
            console.warn('[Layout] Pre-acquire audio failed:', audioErr.name, audioErr.message);
          }

          await ringCentralService.makeCall(number);
          console.log('[Layout] Call placed successfully to', number);

          // Clean up pre-acquired stream if makeCall created its own
          if (audioStream) {
            audioStream.getTracks().forEach(t => t.stop());
          }
        } catch (err) {
          console.error('[Layout] Failed to place call:', err.name, err.message, err);
          // Fall back to auto-dial flag — VoipPage will auto-dial on mount
          sessionStorage.setItem('virgo_auto_dial', 'true');
          window.dispatchEvent(new CustomEvent('virgo-dial-number', { detail: { number, autoDial: true } }));
        }
      } else {
        console.warn('[Layout] SIP not registered — setting auto-dial for when ready');
        sessionStorage.setItem('virgo_auto_dial', 'true');
        window.dispatchEvent(new CustomEvent('virgo-dial-number', { detail: { number, autoDial: true } }));
      }
    };
    window.VirgoNative.onMailCompose = (address) => {
      console.log('[Layout] VirgoNative.onMailCompose:', address);
      sessionStorage.setItem('virgo_compose_email', address);
      window.dispatchEvent(new CustomEvent('virgo-compose-email', { detail: { address } }));
    };

    return () => {
      delete window.VirgoNative?.onPhoneNumber;
      delete window.VirgoNative?.dialNumber;
      delete window.VirgoNative?.onMailCompose;
    };
  }, []);

  // Request notification permission on mount
  useEffect(() => {
    if ('Notification' in window && Notification.permission === 'default') {
      Notification.requestPermission();
    }
  }, []);

  // Auto-dismiss post-call panel after 10 seconds
  useEffect(() => {
    if (!postCallData) return;
    const t = setTimeout(() => setPostCallData(null), 10000);
    return () => clearTimeout(t);
  }, [postCallData]);

  // Global WebSocket connection for incoming calls
  useMailWebSocket({
    onIncomingCall: handleIncomingCall,
    onCallEnded: handleCallEnded,
    onVisageCall: handleVisageCall,
    enabled: true,
  });

  const handleMicPreferenceComplete = (alwaysOn) => {
    setEnabled(alwaysOn);
  };

  return (
    <div className="flex h-screen bg-virgo-navy-900">
      {/* Incoming call popup — rich CIC or fallback banner */}
      {incomingCall && (
        <IncomingCallPopup
          incomingCall={incomingCall}
          onAnswer={handleAnswerClick}
          onDismiss={handleDismissCall}
          onStartIntake={() => {
            const callerPhone = incomingCall?.callData?.from_number || '';
            handleDismissCall();
            navigate(`/voip?intake=1&phone=${encodeURIComponent(callerPhone)}`);
          }}
        />
      )}
      {/* Post-call summary panel */}
      {postCallData && (
        <PostCallPanel
          callData={postCallData}
          onDismiss={() => setPostCallData(null)}
          onOpenCallLog={() => navigate('/voip')}
        />
      )}
      <Sidebar />
      <div className="flex-1 flex flex-col overflow-hidden">
        <div className="flex items-center"><div className="flex-1"><HeaderBar /></div><div className="px-3 flex items-center gap-2">
              {sipRegistered && (
                <div className="w-2 h-2 rounded-full bg-green-400 animate-pulse" title="Phone Online" />
              )}
              <button
                onClick={() => { setPhoneCall(null); setPhoneOpen(!phoneOpen); }}
                className="p-1.5 rounded hover:bg-white/10 text-gray-400 hover:text-cyan-400 transition-colors"
                title="Planchette"
              >
                <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"></path></svg>
              </button>
              <NotificationCenter />
            </div></div>
        <SupportModeBanner />
        <ProfileCompletionBanner />
        <main className={"flex-1 bg-gradient-to-br from-virgo-navy-900 via-virgo-navy-800 to-virgo-navy-900 " + (isFullBleed ? "flex flex-col overflow-hidden" : "overflow-y-auto")}>
          {isFullBleed ? children : (
            <div className="container mx-auto px-6 py-8">
              {children}
            </div>
          )}
        </main>
      </div>
      {/* Visage meeting invite overlay */}
      {visageCall && (
        <div className="fixed inset-0 z-[9998] flex items-center justify-center bg-black/50 backdrop-blur-sm">
          <div className="bg-virgo-navy-700 rounded-2xl p-8 max-w-sm w-full mx-4 border border-cyan-500/50 shadow-2xl shadow-cyan-500/20 animate-pulse-slow">
            <div className="text-center mb-6">
              <div className="w-20 h-20 rounded-full bg-gradient-to-br from-cyan-500 to-indigo-600 flex items-center justify-center mx-auto mb-4">
                <span className="text-3xl text-white font-bold">
                  {visageCall.caller_name?.charAt(0)?.toUpperCase() || 'V'}
                </span>
              </div>
              <h2 className="text-xl font-bold text-white mb-1">
                {visageCall.caller_name}
              </h2>
              <p className="text-virgo-teal-400">
                is inviting you to join
              </p>
              <p className="text-cyan-400 font-medium text-lg mt-1">
                {visageCall.room_name}
              </p>
            </div>
            <div className="flex gap-3">
              <button
                onClick={handleDeclineVisageCall}
                className="flex-1 px-6 py-3 bg-red-500/20 text-red-400 border border-red-500/30 rounded-xl hover:bg-red-500/30 transition font-medium"
              >
                Decline
              </button>
              <button
                onClick={handleAcceptVisageCall}
                className="flex-1 px-6 py-3 bg-gradient-to-r from-cyan-500 to-indigo-600 text-white rounded-xl hover:from-cyan-600 hover:to-indigo-700 transition font-medium"
              >
                Join Meeting
              </button>
            </div>
          </div>
        </div>
      )}
      <AstraeaBubble />
      <VoiceIndicator />
      <PhoneSidebar isOpen={phoneOpen} onClose={() => setPhoneOpen(false)} initialCall={phoneCall} />
      <MicPreferencePrompt onComplete={handleMicPreferenceComplete} />
      <CalendarNotifications />
    </div>
  );
};

export default Layout;
